import * as React from "react";
import * as Sections from ".";
import { ITabProps } from "../";
import { IToolFrameProps } from "../../ToolFrame";
import { Tooltip } from "@material-ui/core";

export class SectionLoader {
    private readonly _context: Readonly<ITabProps>;

    constructor(context: Readonly<ITabProps>) {
        this._context = context;
    }

    public getSection(
        sectionProps: Sections.IBaseSectionProps,
        sectionType: string,
    ): React.ReactNode {
        let section: React.ReactNode;

        switch (sectionType) {
            case `ToolMenu`:
                section = this._toolMenu(
                    sectionProps as Sections.IMenuSectionProps,
                );
                break;
            case `About`:
                section = this._about(sectionProps);
                break;
            case `Notifications`:
                section = this._notifications(sectionProps);
                break;
            case `ViewShare`:
                section = this._viewShare(sectionProps);
                break;
            case `MyProfile`:
                section = this._myProfile(sectionProps);
                break;
            case `Logout`:
                section = this._logout(sectionProps);
                break;
            case `MetriSection`:
                section = this._metriSection(sectionProps);
                break;
            case `SmartSupport`:
                section = this._smartSupport(sectionProps);
                break;
            case `OfflineDataExporter`:
                section = this._ode(sectionProps);
                break;
            case `ReleaseNotes`:
                section = this._releaseNotes(
                    sectionProps as Sections.IReleaseNotesSectionProps,
                );
                break;
            case `DataFeedStatus`:
                section = this._dataFeedStatus(
                    sectionProps as Sections.IDataFeedStatusSectionProps,
                );
                break;
            case `CentralDocumentRepository`:
                section = this._centralDocumentRepository(sectionProps);
                break;
            case `Tenancy`:
                section = this._tenancy(sectionProps);
                break;
            default:
                section = null;
                break;
        }
        if (sectionProps.tooltip) {
            section = (
                <Tooltip
                    key={`tooltip-${sectionProps.moduleID}`}
                    placement={`right`}
                    title={sectionProps.tooltip}
                >
                    <div>{section as React.ReactElement}</div>
                </Tooltip>
            );
        }
        return section;
    }

    private readonly _toolMenu = (
        props: Sections.IMenuSectionProps,
    ): React.ReactNode => {
        const containsTool: (
            menuItem: Sections.IMenuSectionProps,
            tool: IToolFrameProps,
        ) => boolean = (
            menuItem: Sections.IMenuSectionProps,
            tool: IToolFrameProps,
        ): boolean => {
            for (let i = 0; i < menuItem.subSections.length; i++) {
                if (
                    menuItem.subSections[i].tools.find(
                        (t) => t.moduleID === tool.moduleID,
                    ) !== undefined
                ) {
                    return true;
                }
            }
            return false;
        };

        // If the section is selected...
        let isActive = false;

        if (
            this._context.activeSections.find(
                (s) => s.moduleID === props.moduleID,
            )
        ) {
            isActive = true;
        }
        // Or contains the selected tool...
        else if (containsTool(props, this._context.activeTool)) {
            // Then set it as the active section.
            isActive = true;
        }

        return (
            <Sections.MenuSection
                bindToolToFrame={this._context.bindToolToFrame}
                isActive={isActive}
                key={props.moduleID}
                openSection={this._context.openSection}
                toggleSection={this._context.toggleSection}
                updateSection={this._context.updateSection}
                {...props}
            />
        );
    };

    private readonly _notifications = (
        props: Sections.IBaseSectionProps,
    ): React.ReactNode => {
        let isActive = false;

        if (
            this._context.activeSections.find(
                (s) => s.moduleID === props.moduleID,
            )
        ) {
            isActive = true;
        }

        return (
            <Sections.NotificationSection
                isActive={isActive}
                key={props.moduleID}
                toggleSection={this._context.toggleSection}
                updateSection={this._context.updateSection}
                {...props}
            />
        );
    };

    private readonly _viewShare = (
        props: Sections.IBaseSectionProps,
    ): React.ReactNode => {
        let isActive = false;

        if (
            this._context.activeSections.find(
                (s) => s.moduleID === props.moduleID,
            )
        ) {
            isActive = true;
        }

        return (
            <Sections.ViewShareSection
                isActive={isActive}
                key={props.moduleID}
                toggleSection={this._context.toggleSection}
                updateSection={this._context.updateSection}
                {...props}
            />
        );
    };

    private readonly _myProfile = (
        props: Sections.IBaseSectionProps,
    ): React.ReactNode => {
        let isActive = false;

        if (
            this._context.activeSections.find(
                (s) => s.moduleID === props.moduleID,
            )
        ) {
            isActive = true;
        }

        return (
            <Sections.UserProfileSection
                isActive={isActive}
                key={props.moduleID}
                toggleSection={this._context.toggleSection}
                updateSection={this._context.updateSection}
                {...props}
            />
        );
    };

    private readonly _logout = (
        props: Sections.IBaseSectionProps,
    ): React.ReactNode => (
        <Sections.LogoutSection
            isActive={false}
            key={props.moduleID}
            toggleSection={this._context.toggleSection}
            updateSection={this._context.updateSection}
            {...props}
        />
    );

    private readonly _metriSection = (
        props: Sections.IBaseSectionProps,
    ): React.ReactNode => (
        <Sections.MetriSection
            isActive={false}
            key={props.moduleID}
            toggleSection={this._context.toggleSection}
            updateSection={this._context.updateSection}
            {...props}
        />
    );

    private readonly _smartSupport = (
        props: Sections.IBaseSectionProps,
    ): React.ReactNode => {
        let isActive = false;

        if (
            this._context.activeSections.find(
                (s) => s.moduleID === props.moduleID,
            )
        ) {
            isActive = true;
        }

        return (
            <Sections.SmartSupportSection
                isActive={isActive}
                key={props.moduleID}
                toggleSection={this._context.toggleSection}
                updateSection={this._context.updateSection}
                {...props}
            />
        );
    };

    private readonly _ode = (
        props: Sections.IBaseSectionProps,
    ): React.ReactNode => {
        let isActive = false;

        if (
            this._context.activeSections.find(
                (s) => s.moduleID === props.moduleID,
            )
        ) {
            isActive = true;
        }

        return (
            <Sections.ODESection
                isActive={isActive}
                key={props.moduleID}
                toggleSection={this._context.toggleSection}
                updateSection={this._context.updateSection}
                {...props}
            />
        );
    };

    private readonly _about = (
        props: Sections.IBaseSectionProps,
    ): React.ReactNode => {
        let isActive = false;

        if (
            this._context.activeSections.find(
                (s) => s.moduleID === props.moduleID,
            )
        ) {
            isActive = true;
        }

        return (
            <Sections.AboutSection
                isActive={isActive}
                key={props.moduleID}
                toggleSection={this._context.toggleSection}
                updateSection={this._context.updateSection}
                {...props}
            />
        );
    };

    private readonly _centralDocumentRepository = (
        props: Sections.IBaseSectionProps,
    ): React.ReactNode => {
        let isActive = false;

        if (
            this._context.activeSections.find(
                (s) => s.moduleID === props.moduleID,
            )
        ) {
            isActive = true;
        }

        return (
            <Sections.CentralDocumentRepositorySection
                isActive={isActive}
                key={props.moduleID}
                toggleSection={this._context.toggleSection}
                updateSection={this._context.updateSection}
                {...props}
            />
        );
    };

    private readonly _releaseNotes = (
        props: Sections.IReleaseNotesSectionProps,
    ): React.ReactNode => {
        const containsTool: (
            menuItem: Sections.IReleaseNotesSectionProps,
            tool: IToolFrameProps,
        ) => boolean = (
            menuItem: Sections.IReleaseNotesSectionProps,
            tool: IToolFrameProps,
        ): boolean => {
            for (let i = 0; i < menuItem.subSections.length; i++) {
                if (
                    menuItem.subSections[i].tools.find(
                        (t) => t.moduleID === tool.moduleID,
                    ) !== undefined
                ) {
                    return true;
                }
            }
            return false;
        };

        // If the section is selected...
        let isActive = false;

        if (
            this._context.activeSections.find(
                (s) => s.moduleID === props.moduleID,
            )
        ) {
            isActive = true;
        }
        // Or contains the selected tool...
        else if (containsTool(props, this._context.activeTool)) {
            // Then set it as the active section.
            isActive = true;
        }

        return (
            <Sections.ReleaseNotesSection
                bindToolToFrame={this._context.bindToolToFrame}
                isActive={isActive}
                key={props.moduleID}
                openSection={this._context.openSection}
                toggleSection={this._context.toggleSection}
                updateSection={this._context.updateSection}
                {...props}
            />
        );
    };

    private readonly _dataFeedStatus = (
        props: Sections.IDataFeedStatusSectionProps,
    ): React.ReactNode => {
        const containsTool: (
            menuItem: Sections.IDataFeedStatusSectionProps,
            tool: IToolFrameProps,
        ) => boolean = (
            menuItem: Sections.IDataFeedStatusSectionProps,
            tool: IToolFrameProps,
        ): boolean => {
            for (let i = 0; i < menuItem.subSections.length; i++) {
                if (
                    menuItem.subSections[i].tools.find(
                        (t) => t.moduleID === tool.moduleID,
                    ) !== undefined
                ) {
                    return true;
                }
            }
            return false;
        };

        // If the section is selected...
        let isActive = false;

        if (
            this._context.activeSections.find(
                (s) => s.moduleID === props.moduleID,
            )
        ) {
            isActive = true;
        }
        // Or contains the selected tool...
        else if (containsTool(props, this._context.activeTool)) {
            // Then set it as the active section.
            isActive = true;
        }

        return (
            <Sections.DataFeedStatusSection
                bindToolToFrame={this._context.bindToolToFrame}
                isActive={isActive}
                key={props.moduleID}
                openSection={this._context.openSection}
                toggleSection={this._context.toggleSection}
                updateSection={this._context.updateSection}
                {...props}
            />
        );
    };

    private readonly _tenancy = (
        props: Sections.IBaseSectionProps,
    ): React.ReactNode => (
        <Sections.TenancySection
            isActive={false}
            key={props.moduleID}
            toggleSection={this._context.toggleSection}
            updateSection={this._context.updateSection}
            {...props}
        />
    );
}
